/**
 * https://auth0.com/blog/building-an-audio-player-app-with-angular-and-rxjs/
 * https://github.com/imsingh/auth0-audio/blob/master/src/app/services/audio.service.ts
 *
 * 주의: playSteam() 함수로 재생이 끝나면 finalize + unscribe가 되지 않음에 주의해야 함.
 *      필요시 명시적으로 unscribe를 호출해야 함.
 */
import { Injectable } from '@angular/core';
// import * as moment from 'moment';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {StreamState} from '../../interfaces/stream-state';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private stop$ = new Subject<void>();
  private audioObj = new Audio();
  audioEvents = [
    'ended',
    'error',
    'play',
    'playing',
    'pause',
    'timeupdate',
    'canplay',
    'loadedmetadata',
    'loadstart',
  ];
  private state: StreamState = {
    playing: false,
    readableCurrentTime: '',
    readableDuration: '',
    duration: undefined,
    currentTime: undefined,
    canplay: false,
    error: false,
  };

  private streamObservable(url): Observable<Event> {
    return new Observable(observer => {
      // Play audio
      this.audioObj.src = url;
      this.audioObj.load();
      this.audioObj.play().then().catch(err => {
        console.error(err);
        observer.error(err);
      });

      const handler = (event: Event) => {
        this.updateStateEvents(event);
        observer.next(event);
      };

      this.addEvents(this.audioObj, this.audioEvents, handler);

      return () => {
        // Stop Playing
        this.audioObj.pause();
        this.audioObj.currentTime = 0;

        // remove event listeners
        this.removeEvents(this.audioObj, this.audioEvents, handler);
      };
    });
  }

  private addEvents(obj, events: Array<string>, handler): void {
    // console.warn('AudioService::addEvents()', events);
    events.forEach(event => {
      obj.addEventListener(event, handler);
    });
  }

  private removeEvents(obj, events: Array<string>, handler): void {
    // console.warn('AudioService::removeEvents()', events);
    events.forEach(event => {
      obj.removeEventListener(event, handler);
    });
  }

  public playStream(url: string): Observable<Event> {
    // console.warn('AudioService::playStream()', url);
    return this.streamObservable(url).pipe(takeUntil(this.stop$));
  }

  public play(): Promise<void> {
    return this.audioObj.play();
  }

  public pause(): void {
    this.audioObj.pause();
  }

  public stop(): void {
    // console.warn('AudioService::stop()');
    this.stop$.next();
  }

  public seekTo(seconds: number): void {
    this.audioObj.currentTime = seconds;
  }

  // public formatTime(time: number, format: string = 'HH:mm:ss'): string {
  //   const momentTime = time * 1000;
  //   return moment.utc(momentTime).format(format);
  // }

  private stateChange: BehaviorSubject<StreamState> = new BehaviorSubject<StreamState>(this.state);

  private updateStateEvents(event: Event): void {
    switch(event.type) {
      case 'canplay':
        this.state.duration = this.audioObj.duration;
        // this.state.readableDuration = this.formatTime(this.state.duration);
        this.state.canplay = true;
        break;
      case 'playing':
        this.state.playing = true;
        break;
      case 'pause':
        this.state.playing = false;
        break;
      case 'timeupdate':
        this.state.currentTime = this.audioObj.currentTime;
        // this.state.readableCurrentTime = this.formatTime(this.state.currentTime);
        break;
      case 'error':
        this.resetState();
        this.state.error = true;
        break;
    }
    this.stateChange.next(this.state);
  }

  private resetState(): void {
    this.state = {
      playing: false,
      readableCurrentTime: '',
      readableDuration: '',
      duration: undefined,
      currentTime: undefined,
      canplay: false,
      error: false,
    };
  }

  public getState(): Observable<StreamState> {
    return this.stateChange.asObservable();
  }

  constructor() { }
}
